import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from "../hooks/use-notifications";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
};

/** Link each notification type to where the user should land. */
const notificationLink = (n) => {
  if (n.invoice_id) {
    return `/dashboard/business-invoices?openInvoice=${n.invoice_id}&tab=comments`;
  }
  return null;
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { data: unread = 0 } = useUnreadNotificationCount();
  const { data, isLoading } = useNotifications({ limit: 20 }, { enabled: open });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const items = data?.data || [];

  const handleOpen = (n) => {
    if (!n.read_at) markRead.mutate(n.id);
    setAnchorEl(null);
    const link = notificationLink(n);
    if (link) navigate(link);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: "text.secondary" }}>
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 380, maxHeight: 480 } }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle2">Notifications</Typography>
          <Button
            size="small"
            disabled={!unread || markAll.isLoading}
            onClick={() => markAll.mutate()}
            sx={{ fontSize: 12 }}
          >
            Mark all read
          </Button>
        </Box>
        <Divider />
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: "center" }}>
            You're all caught up.
          </Typography>
        ) : (
          items.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => handleOpen(n)}
              sx={{
                alignItems: "flex-start",
                gap: 1.25,
                whiteSpace: "normal",
                bgcolor: n.read_at ? "transparent" : "action.hover",
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 18, mt: 0.4, color: n.read_at ? "text.disabled" : "primary.main" }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: n.read_at ? 400 : 600, lineHeight: 1.3 }}>
                  {n.title}
                </Typography>
                {n.body && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {n.body}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
                  {timeAgo(n.created_at)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
